import type { PluginAttachmentValue } from '@runloop/reflex-contract';
import type { AgentEnvVar } from '@reflex/shared';

/**
 * True when a plugin attachment's `config` carries any non-empty data.
 *
 * Attachments are added to form state as soon as the user clicks "Add" in
 * the picker, which produces an entry with a placeholder config (often
 * just `{}`). Without this guard we'd persist those placeholders to the
 * server, where they'd appear as "configured" attachments with no
 * meaningful payload. Both the Launch dialog and the Persona form filter
 * with this helper before submitting.
 */
export function hasMeaningfulConfig(config: unknown): boolean {
  if (config == null) return false;
  if (typeof config !== 'object') return true;
  const values = Object.values(config as Record<string, unknown>);
  if (values.length === 0) return false;
  return values.some((v) => {
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'string') return v.length > 0;
    return true;
  });
}

/**
 * Same logic the Launch dialog and Persona form use to drop unfinished
 * env-var rows before sending them to the server: an empty key is treated
 * as still being authored, and a row marked `isSecret` without a
 * `secretRef` means the user opened the secret editor but never picked
 * one — both should be skipped silently.
 */
export function sanitizeEnvVars(envVars: AgentEnvVar[]): AgentEnvVar[] {
  return envVars.filter((v) => {
    if (v.key.trim().length === 0) return false;
    if (v.isSecret && !v.secretRef) return false;
    return true;
  });
}

/**
 * Filter `attachmentValues` down to attachments with non-empty configs,
 * preserving the original order. Returned as an array (the form-state map
 * keeps them keyed by attachmentId for fast lookups, but the server takes
 * a list).
 */
export function sanitizeAttachments(
  attachmentValues: Record<string, PluginAttachmentValue>,
): PluginAttachmentValue[] {
  return Object.values(attachmentValues).filter((att) => hasMeaningfulConfig(att.config));
}
